import {
  GetUserOrdersDetailsResponseType,
  GetUserProgramInstituteCoursesResponseType
} from '@nw/shared/data-access';

export const getLatestUserProgramInstituteCourseRow = (
  userProgramInstituteCoursesData?: GetUserProgramInstituteCoursesResponseType | null
) => {
  const userProgramInstituteCourses =
    userProgramInstituteCoursesData?.user_program_institute_courses;

  if (
    !Array.isArray(userProgramInstituteCourses) ||
    !userProgramInstituteCourses.length
  ) {
    return null;
  }

  return [...userProgramInstituteCourses].sort((a, b) => {
    return (
      new Date(b.registered_at).getTime() - new Date(a.registered_at).getTime()
    );
  })[0];
};

const getAmountFromLeastSubunit = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value / 100;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return parsedValue / 100;
    }
  }
  return null;
};

export const gteSeatReservationDetailsFromUserOrdersDetails = (
  userOrdersDetails: GetUserOrdersDetailsResponseType['user_orders_details'],
  productCode: string
) => {
  if (!userOrdersDetails?.length || !productCode) {
    return null;
  }
  const niatProgramOrders = userOrdersDetails.find(
    (orderDetail) => orderDetail?.program === 'NIAT'
  );
  const seatReservationOrder = niatProgramOrders?.registered?.find(
    (orderDetail) => orderDetail?.product_code === productCode
  );
  const amountPaid = getAmountFromLeastSubunit(
    seatReservationOrder?.user_order_details?.amount_paid_in_least_subunit
  );
  return amountPaid;
};
export const getInstituteDisplayLabel = (
  instituteId: string,
  availableProgramInstitutesData: any
) => {
  return (
    availableProgramInstitutesData?.institutes?.find(
      (inst) => inst.institute_id === instituteId
    )?.institute_name ?? '-'
  );
};

export type PaymentHistoryRow = {
  date: string;
  mobileDate: string;
  documentNumber: string;
  amount: number;
  status: string;
  statusTone: 'success' | 'pending' | 'failed';
};

const formatPaymentHistoryDate = (value: unknown): string => {
  const date = new Date(String(value ?? ''));
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const rawDate = date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return rawDate.replace(/ (\d{4})$/, ', $1');
};

const getOrdinalDayValue = (day: number): string => {
  const moduloTen = day % 10;
  const moduloHundred = day % 100;
  if (moduloHundred >= 11 && moduloHundred <= 13) {
    return `${day}th`;
  }
  if (moduloTen === 1) {
    return `${day}st`;
  }
  if (moduloTen === 2) {
    return `${day}nd`;
  }
  if (moduloTen === 3) {
    return `${day}rd`;
  }
  return `${day}th`;
};

const formatPaymentHistoryMobileDate = (value: unknown): string => {
  const date = new Date(String(value ?? ''));
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const month = date.toLocaleDateString('en-IN', { month: 'short' });
  const year = date.getFullYear();
  return `${getOrdinalDayValue(date.getDate())} ${month}, ${year}`;
};

const getPaymentHistoryStatusMeta = (
  rawStatus: unknown
): {
  label: string;
  tone: PaymentHistoryRow['statusTone'];
} => {
  const normalizedStatus = String(rawStatus ?? '')
    .toUpperCase()
    .trim();
  if (
    normalizedStatus === 'SUCCESS' ||
    normalizedStatus === 'PAID' ||
    normalizedStatus === 'CAPTURED' ||
    normalizedStatus === 'COMPLETED'
  ) {
    return { label: 'Success', tone: 'success' };
  }
  if (
    normalizedStatus === 'FAILED' ||
    normalizedStatus === 'CANCELLED' ||
    normalizedStatus === 'ABORTED'
  ) {
    return { label: 'Failed', tone: 'failed' };
  }
  return { label: 'Pending', tone: 'pending' };
};

export const getEnrollmentPaymentHistoryRowsFromUserOrdersDetails = (
  userOrdersDetails: GetUserOrdersDetailsResponseType['user_orders_details'],
  productCode: string
): PaymentHistoryRow[] => {
  if (!Array.isArray(userOrdersDetails) || !productCode) {
    return [];
  }

  return userOrdersDetails
    .flatMap((programOrder) => programOrder?.registered ?? [])
    .filter((registeredOrder) => registeredOrder?.product_code === productCode)
    .map((registeredOrder) => {
      const orderDetails = registeredOrder?.user_order_details;
      const statusMeta = getPaymentHistoryStatusMeta(
        Number(orderDetails?.amount_paid_in_least_subunit ?? 0) > 0
          ? 'SUCCESS'
          : 'PENDING'
      );
      const timestamp = new Date(
        String(
          orderDetails?.order_active_at ??
            orderDetails?.starts_on_datetime ??
            ''
        )
      ).getTime();
      return {
        timestamp: Number.isNaN(timestamp) ? 0 : timestamp,
        date: formatPaymentHistoryDate(
          orderDetails?.order_active_at ?? orderDetails?.starts_on_datetime
        ),
        mobileDate: formatPaymentHistoryMobileDate(
          orderDetails?.order_active_at ?? orderDetails?.starts_on_datetime
        ),
        documentNumber: String(orderDetails?.order_id ?? '—').trim() || '—',
        amount:
          getAmountFromLeastSubunit(
            orderDetails?.amount_paid_in_least_subunit
          ) ??
          registeredOrder?.amount ??
          0,
        status: statusMeta.label,
        statusTone: statusMeta.tone
      };
    })
    .sort((first, second) => second.timestamp - first.timestamp)
    .map(({ timestamp, ...row }) => row);
};

export const getEnrollmentPaymentHistoryRowsFromInstallmentPayments = (
  payments: any[]
): PaymentHistoryRow[] => {
  if (!Array.isArray(payments)) {
    return [];
  }

  return payments
    .map((payment) => {
      const timestamp = new Date(
        String(payment?.payment_created_datetime ?? '')
      ).getTime();
      const statusMeta = getPaymentHistoryStatusMeta(payment?.payment_status);
      return {
        timestamp: Number.isNaN(timestamp) ? 0 : timestamp,
        date: formatPaymentHistoryDate(payment?.payment_created_datetime),
        mobileDate: formatPaymentHistoryMobileDate(
          payment?.payment_created_datetime
        ),
        documentNumber: String(payment?.payment_ref_no ?? '—').trim() || '—',
        amount: Number(payment?.payment_amount ?? 0),
        status: statusMeta.label,
        statusTone: statusMeta.tone
      };
    })
    .sort((first, second) => second.timestamp - first.timestamp)
    .map(({ timestamp, ...row }) => row);
};
export const parseRupeeAmount = (value: unknown): number | null => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return parsedValue;
    }
  }
  return null;
};
